import { useState } from "react";
import { Facebook, Instagram, Mail, Trees, Loader2, CheckCircle, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubscribe = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Por favor ingresa un email válido");
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("subscribe-newsletter", {
        body: { email, source: "website_footer" },
      });

      if (error) throw error;

      if (data?.success) {
        setIsSuccess(true);
        setEmail("");
        toast.success(data.message || "¡Gracias por suscribirte!");
        // Reset success state after 5 seconds
        setTimeout(() => setIsSuccess(false), 5000);
      } else {
        throw new Error(data?.error || "Error al suscribirse");
      }
    } catch (error: any) {
      console.error("Newsletter subscription error:", error);
      toast.error(error.message || "Error al procesar tu suscripción");
    } finally {
      setIsLoading(false);
    }
  };
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    empresa: [
      { name: "Sobre Nosotros", href: "/sobre-nosotros" },
    ],
    soluciones: [
      { name: "Cebos e Insecticidas", href: "/productos?category=cebos-geles-insecticidas" },
      { name: "Control de Roedores", href: "/productos?category=control-roedores" },
      { name: "Trampas Lumínicas", href: "/productos?category=trampas-luminicas-led" },
      { name: "Ver Todos", href: "/productos" },
    ],
    recursos: [
      { name: "Catálogo de Productos", href: "/productos" },
      { name: "Blog", href: "/blog" },
      { name: "Solicitar Cotización", href: "/cotizacion" },
      { name: "Contacto", href: "/#contacto" },
    ],
  };

  const businessAddress = {
    text: "Gral. Victoriano Zepeda 1, Observatorio, Miguel Hidalgo, 11860 Ciudad de México, CDMX",
    mapsUrl: "https://maps.app.goo.gl/UY6pjN4qP8wvihsBA",
  };

  // Custom TikTok icon
  const TikTokIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
  );

  // Custom X (formerly Twitter) icon
  const XIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );

  const socialLinks = [
    { icon: Facebook, href: "https://www.facebook.com/EcontrolMX/", label: "Facebook" },
    { icon: XIcon, href: "https://x.com/ECONTROLMX", label: "X", isCustom: true },
    { icon: Instagram, href: "https://www.instagram.com/econtrol.mx?igsh=bHppeGdpcHV0ODc1", label: "Instagram" },
    { icon: TikTokIcon, href: "https://www.tiktok.com/@econtrolmx", label: "TikTok", isCustom: true },
  ];

  return (
    <footer role="contentinfo" className="bg-[hsl(var(--footer-bg))] text-white">
      {/* Top wave decoration */}
      <div className="bg-background">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
          <path d="M0 0L60 5C120 10 240 20 360 25C480 30 600 30 720 27.5C840 25 960 20 1080 17.5C1200 15 1320 15 1380 15L1440 15V60H1380C1320 60 1200 60 1080 60C960 60 840 60 720 60C600 60 480 60 360 60C240 60 120 60 60 60H0Z" fill="hsl(var(--footer-bg))"/>
        </svg>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-12 lg:mb-16">
          {/* Brand Section */}
          <div className="sm:col-span-2 lg:col-span-1">
            <h2 className="text-2xl font-bold text-white mb-4">MYLVA ECONTROL</h2>
            <p className="text-white/75 mb-8 max-w-sm leading-relaxed text-lg">
              Líderes en soluciones innovadoras y sustentables para el control de plagas. 
              Comprometidos con la calidad, el medio ambiente y tu tranquilidad.
            </p>
            <div className="flex gap-4">
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                return (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-2xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all duration-300 hover:scale-105"
                    aria-label={social.label}
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Links Sections */}
          <div>
            <h3 className="font-bold text-white mb-6 text-lg">Empresa</h3>
            <ul className="space-y-4">
              {footerLinks.empresa.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.href}
                    className="text-white/70 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-white mb-6 text-lg">Soluciones</h3>
            <ul className="space-y-4">
              {footerLinks.soluciones.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.href}
                    className="text-white/70 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-white mb-6 text-lg">Recursos</h3>
            <ul className="space-y-4">
              {footerLinks.recursos.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.href}
                    className="text-white/70 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Location Section - Full width at bottom */}
        <div className="border-t border-white/10 pt-8 mb-8">
          <a 
            href={businessAddress.mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 text-white/70 hover:text-white transition-colors group"
            aria-label="Ver ubicación en Google Maps"
          >
            <MapPin className="w-5 h-5 flex-shrink-0 text-cta group-hover:scale-110 transition-transform" />
            <span className="text-center">{businessAddress.text}</span>
          </a>
        </div>

        {/* Newsletter Section */}
        <div className="border-t border-white/10 pt-12 mb-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">
              Mantente Informado
            </h3>
            <p className="text-white/70 mb-8 text-lg">
              Recibe noticias, tips y ofertas especiales directamente en tu correo.
            </p>
            {isSuccess ? (
              <div className="flex items-center justify-center gap-2 text-green-400 py-4">
                <CheckCircle className="w-6 h-6" />
                <span className="text-lg font-medium">¡Revisa tu correo para descargar el catálogo!</span>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <label htmlFor="newsletter-email" className="sr-only">Correo electrónico para suscripción</label>
                <input
                  id="newsletter-email"
                  type="email"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSubscribe()}
                  disabled={isLoading}
                  className="flex-1 px-5 py-3.5 sm:px-6 sm:py-4 rounded-xl sm:rounded-2xl border border-white/20 bg-white/10 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-white/30 min-h-[44px] disabled:opacity-50"
                  aria-describedby="newsletter-description"
                />
                <button 
                  type="button"
                  onClick={handleSubscribe}
                  disabled={isLoading}
                  className="px-8 py-3.5 sm:px-10 sm:py-4 bg-cta text-cta-foreground rounded-xl sm:rounded-2xl font-semibold hover:bg-cta-hover transition-all hover:scale-[1.02] shadow-lg min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  aria-label="Suscribirse al boletín"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    "Suscribirse"
                  )}
                </button>
                <p id="newsletter-description" className="sr-only">Recibe noticias, tips y ofertas especiales</p>
              </div>
            )}
          </div>
        </div>

        {/* Eco Badge */}
        <div className="flex items-center justify-center gap-3 mb-10" aria-label="Compromiso ambiental">
          <Trees className="w-6 h-6 text-white/60" aria-hidden="true" />
          <span className="text-white/60 text-sm">Comprometidos con el medio ambiente y la sustentabilidad</span>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-10 text-center text-sm text-white/60">
          <p>© {currentYear} ECONTROL. Todos los derechos reservados.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
